import { onCLS, onINP, onFCP, onLCP, onTTFB, type Metric } from 'web-vitals';

export interface PerformanceMetrics {
  cls: number | null;
  inp: number | null; // Interaction to Next Paint (replaces FID)
  fcp: number | null;
  lcp: number | null;
  ttfb: number | null;
  timestamp: number;
}

export interface BundleAnalysis {
  totalSize: number;
  chunkSizes: Record<string, number>;
  loadTimes: Record<string, number>;
  cacheHitRate: number;
}

class PerformanceService {
  private metrics: PerformanceMetrics = {
    cls: null,
    inp: null,
    fcp: null,
    lcp: null,
    ttfb: null,
    timestamp: Date.now()
  };

  private bundleAnalysis: BundleAnalysis = {
    totalSize: 0,
    chunkSizes: {},
    loadTimes: {},
    cacheHitRate: 0
  };

  private observers: ((metrics: PerformanceMetrics) => void)[] = [];
  private bundleObservers: ((analysis: BundleAnalysis) => void)[] = [];

  constructor() {
    this.initializeWebVitals();
    this.initializeBundleAnalysis();
  }

  private initializeWebVitals() {
    // Collect Core Web Vitals
    onCLS((metric: Metric) => {
      this.metrics.cls = metric.value;
      this.notifyObservers();
    });

    onINP((metric: Metric) => {
      this.metrics.inp = metric.value;
      this.notifyObservers();
    });

    onFCP((metric: Metric) => {
      this.metrics.fcp = metric.value;
      this.notifyObservers();
    });

    onLCP((metric: Metric) => {
      this.metrics.lcp = metric.value;
      this.notifyObservers();
    });

    onTTFB((metric: Metric) => {
      this.metrics.ttfb = metric.value;
      this.notifyObservers();
    });
  }

  private initializeBundleAnalysis() {
    // Analyze bundle performance using Performance API
    if ('performance' in window && 'getEntriesByType' in performance) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'navigation') {
            this.analyzeNavigationTiming(entry as PerformanceNavigationTiming);
          } else if (entry.entryType === 'resource') {
            this.analyzeResourceTiming(entry as PerformanceResourceTiming);
          }
        }
      });

      observer.observe({ entryTypes: ['navigation', 'resource'] });
    }
  }

  private analyzeNavigationTiming(entry: PerformanceNavigationTiming) {
    // Calculate page load metrics
    const loadTime = entry.loadEventEnd - entry.loadEventStart;
    const domContentLoaded = entry.domContentLoadedEventEnd - entry.domContentLoadedEventStart;
    
    this.bundleAnalysis.loadTimes = {
      ...this.bundleAnalysis.loadTimes,
      pageLoad: loadTime,
      domContentLoaded: domContentLoaded,
      firstByte: entry.responseStart - entry.requestStart
    };

    this.notifyBundleObservers();
  }

  private analyzeResourceTiming(entry: PerformanceResourceTiming) {
    // Analyze JavaScript chunks and assets
    const url = new URL(entry.name);
    const filename = url.pathname.split('/').pop() || 'unknown';
    
    if (filename.includes('.js') || filename.includes('.css')) {
      const size = entry.transferSize || entry.encodedBodySize || 0;
      const loadTime = entry.responseEnd - entry.requestStart;
      
      this.bundleAnalysis.chunkSizes[filename] = size;
      this.bundleAnalysis.loadTimes[filename] = loadTime;
      this.bundleAnalysis.totalSize += size;

      // Calculate cache hit rate
      const fromCache = entry.transferSize === 0 && entry.encodedBodySize > 0;
      if (fromCache) {
        this.bundleAnalysis.cacheHitRate += 1;
      }

      this.notifyBundleObservers();
    }
  }

  private notifyObservers() {
    this.observers.forEach(observer => observer({ ...this.metrics }));
  }

  private notifyBundleObservers() {
    this.bundleObservers.forEach(observer => observer({ ...this.bundleAnalysis }));
  }

  // Public API
  public getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  public getBundleAnalysis(): BundleAnalysis {
    return { ...this.bundleAnalysis };
  }

  public onMetricsUpdate(callback: (metrics: PerformanceMetrics) => void) {
    this.observers.push(callback);
    return () => {
      const index = this.observers.indexOf(callback);
      if (index > -1) {
        this.observers.splice(index, 1);
      }
    };
  }

  public onBundleAnalysisUpdate(callback: (analysis: BundleAnalysis) => void) {
    this.bundleObservers.push(callback);
    return () => {
      const index = this.bundleObservers.indexOf(callback);
      if (index > -1) {
        this.bundleObservers.splice(index, 1);
      }
    };
  }

  public getPerformanceScore(): number {
    const { cls, inp, fcp, lcp, ttfb } = this.metrics;
    
    // Calculate performance score based on Core Web Vitals thresholds
    let score = 100;
    
    // LCP (Largest Contentful Paint) - Good: <2.5s, Poor: >4s
    if (lcp !== null) {
      if (lcp > 4000) score -= 30;
      else if (lcp > 2500) score -= 15;
    }
    
    // INP (Interaction to Next Paint) - Good: <200ms, Poor: >500ms
    if (inp !== null) {
      if (inp > 500) score -= 25;
      else if (inp > 200) score -= 10;
    }
    
    // CLS (Cumulative Layout Shift) - Good: <0.1, Poor: >0.25
    if (cls !== null) {
      if (cls > 0.25) score -= 25;
      else if (cls > 0.1) score -= 10;
    }
    
    // FCP (First Contentful Paint) - Good: <1.8s, Poor: >3s
    if (fcp !== null) {
      if (fcp > 3000) score -= 15;
      else if (fcp > 1800) score -= 8;
    }
    
    // TTFB (Time to First Byte) - Good: <800ms, Poor: >1800ms
    if (ttfb !== null) {
      if (ttfb > 1800) score -= 5;
      else if (ttfb > 800) score -= 2;
    }
    
    return Math.max(0, score);
  }

  public getBundleScore(): number {
    const { totalSize, cacheHitRate } = this.bundleAnalysis;
    const chunkCount = Object.keys(this.bundleAnalysis.chunkSizes).length;
    
    let score = 100;
    
    // Bundle size penalty - Target: <500KB, Poor: >1MB
    if (totalSize > 1024 * 1024) score -= 30; // >1MB
    else if (totalSize > 500 * 1024) score -= 15; // >500KB
    
    // Too many chunks penalty
    if (chunkCount > 10) score -= 10;
    else if (chunkCount > 5) score -= 5;
    
    // Cache hit rate bonus
    const hitRate = chunkCount > 0 ? cacheHitRate / chunkCount : 0;
    if (hitRate > 0.8) score += 10;
    else if (hitRate > 0.5) score += 5;
    
    return Math.max(0, Math.min(100, score));
  }

  public exportMetrics(): string {
    return JSON.stringify({
      metrics: this.metrics,
      bundleAnalysis: this.bundleAnalysis,
      performanceScore: this.getPerformanceScore(),
      bundleScore: this.getBundleScore(),
      timestamp: Date.now()
    }, null, 2);
  }
}

// Singleton instance
export const performanceService = new PerformanceService();
export default performanceService;